import type { HsRenderObjects } from '../../engine-new/filtering/render-objects.class';
import type { HsPerspectiveCamera } from '../../engine-new/objects/cameras/perspective-camera.class';
import type { HsGraphicLayer } from '../../graphic-render-new/objects/graphic-layer.class';
import type { HsGraphicObject } from '../../graphic-render-new/objects/graphic-object.class';
import type { HsGraphicQueue } from '../../graphic-render-new/objects/graphic-queue.class';
import { HsGraphicPoints } from '../../graphic-render-new/objects/graphic-points.class';
import type { HsCullingFilter } from '../runtime/culling-pass.class';
import type { HsNavigationCulling, HsNavigationMinScreenSize } from './navigation-culling.class';
import type { HsSparseCullingCandidates } from './sparse-culling-candidates.function';
import type { HsSpatialCulling } from './spatial-culling.class';

type HsViewerVisibleObjects = HsRenderObjects<HsGraphicObject, HsGraphicLayer>;

/** Screen-size culling of one pass: skip objects smaller than `threshold` CSS pixels while navigating. */
export interface HsViewerSizeCulling {
  canvasSize: { realWidth: number; realHeight: number } | null;
  threshold: HsNavigationMinScreenSize;
  navigating: boolean;
  /** Time of this pass, for the navigation culling refresh interval. */
  now: number;
}

export interface HsViewerCullingFilterOptions {
  /**
   * The queue's objects in a stable draw order, or null to test them in insertion order. Only an ordered
   * queue uses the sphere tree, because the tree indexes positions in that order.
   */
  order(queue: HsGraphicQueue): readonly HsGraphicObject[] | null;
  /** Sphere-tree culling over the ordered objects; null tests every object against the frustum. */
  spatial?: HsSpatialCulling<HsGraphicObject> | null;
  /** Screen-size culling while the camera moves. */
  navigation?: HsNavigationCulling<HsGraphicObject> | null;
  /** Size culling settings for a pass over `queue`, or null when the queue is not size culled. */
  sizeCulling?(queue: HsGraphicQueue): HsViewerSizeCulling | null;
  /** Objects drawn whenever they are visible, also outside the frustum (debug overlays). */
  alwaysDrawn?(object: HsGraphicObject): boolean;
  /** Objects size culling never skips (selection highlights, markers). */
  keepWhileNavigating?(object: HsGraphicObject): boolean;
}

/**
 * The scene viewer's culling: visible, not deleted objects that meet the frustum, found through a sphere
 * tree over a stable draw order, minus objects too small on screen while the camera moves. Transparent
 * objects go to the sorted list.
 */
export class HsViewerCullingFilter
  implements HsCullingFilter<HsGraphicQueue, HsPerspectiveCamera, HsViewerVisibleObjects>
{
  public constructor(private readonly options: HsViewerCullingFilterOptions) {}

  public filter(queue: HsGraphicQueue, camera: HsPerspectiveCamera, target: HsViewerVisibleObjects): void {
    const { spatial, navigation, alwaysDrawn, keepWhileNavigating } = this.options;
    const orderedObjects: readonly HsGraphicObject[] | null = this.options.order(queue);
    const mask: Uint8Array | null =
      orderedObjects === null || spatial === undefined || spatial === null
        ? null
        : spatial.query(orderedObjects, camera.frustum.planes);
    const candidates: HsSparseCullingCandidates | null =
      orderedObjects === null || mask === null || spatial === undefined || spatial === null
        ? null
        : spatial.selectCandidates(orderedObjects, mask);
    const size: HsViewerSizeCulling | null = this.options.sizeCulling?.(queue) ?? null;
    if (size !== null && navigation !== undefined && navigation !== null) {
      navigation.begin(camera, size.canvasSize, size.threshold, size.navigating, size.now);
    }
    const sizeCulling: boolean = size !== null && size.navigating && navigation !== undefined && navigation !== null;
    const accept = (object: HsGraphicObject, intersection: number | undefined): void => {
      if (!object.visible || object.isDeleted) {
        return;
      }
      // Point cloud blocks the draw budget skipped this frame keep their buffers but are not drawn.
      if (object instanceof HsGraphicPoints && object.budgetSkipped) {
        return;
      }
      const drawnAnyway: boolean = alwaysDrawn?.(object) === true;
      if (
        !drawnAnyway &&
        (intersection === 0 || (intersection !== 1 && !camera.frustum.hasIntersectionWithSphere(object.boundingSphere)))
      ) {
        return;
      }
      if (
        sizeCulling &&
        !drawnAnyway &&
        keepWhileNavigating?.(object) !== true &&
        !(navigation as HsNavigationCulling<HsGraphicObject>).isVisible(object)
      ) {
        return;
      }
      if (object.material.getShader().hasTransparency) {
        target.sortedObjects.push(object);
      } else {
        target.unsortedObjects.push(object);
      }
    };
    // Indexed loops, not a shared for-of over a generator, array or map iterator: the polymorphic iteration
    // roughly doubled this pass for tens of thousands of queued objects during navigation.
    if (orderedObjects !== null && mask !== null && candidates !== null) {
      for (let cursor: number = 0; cursor < candidates.count; cursor++) {
        const index: number = candidates.indices[cursor];
        accept(orderedObjects[index], mask[index]);
      }
    } else if (orderedObjects !== null) {
      for (let index: number = 0; index < orderedObjects.length; index++) {
        accept(orderedObjects[index], mask?.[index]);
      }
    } else {
      for (const object of queue.objects.values()) {
        accept(object, undefined);
      }
    }
  }
}
